import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DeviceDetectorService } from 'ngx-device-detector';
import { TranslateService } from '@ngx-translate/core';
import { BasicInfoService } from 'src/app/services/basic-info.service';
import { CompanyEngagementsModel } from 'src/app/models/company.model';
import { Router } from '@angular/router';
import { CompanyApiService } from 'src/app/services/api/company-api.service';
import { LoginApiService } from 'src/app/services/api/login-api.service';
import { VerifyIdModel, VerifySessionModel } from 'src/app/models/login.model';
import { interval, Observable, Subscription, TimeoutError } from 'rxjs';
import { last, switchMap, takeWhile, timeout } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth.service';
import { AddApplicationResponseModel } from 'src/app/models/applications.model';
import { ApplicationService } from 'src/app/services/application-service';
import { personNumberValidator } from 'src/app/validators/personnumber';
import { personNumberFormatter } from 'src/app/utils/format-personnumber';
import { TrackingService } from 'src/app/services/tracking.service';
import { FlowService } from 'src/app/services/flow.service';

@Component({
  selector: 'app-identification',
  templateUrl: './identification-fi.component.html',
  styleUrls: ['./identification.component.scss'],
})
export class IdentificationComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  loading = false;
  error = '';
  subs = Array<Subscription>();

  constructor(
    protected companyApi: CompanyApiService,
    protected loginApi: LoginApiService,
    protected basicInfoService: BasicInfoService,
    protected deviceService: DeviceDetectorService,
    protected translateService: TranslateService,
    protected router: Router,
    protected authService: AuthService,
    protected applicationService: ApplicationService,
    protected trackingService: TrackingService,
    protected flowService: FlowService
  ) {}

  ngOnInit(): void {
    this.form = new FormGroup({
      personNumber: new FormControl('', [
        Validators.required,
        personNumberValidator(),
      ]),
    });
  }

  ngOnDestroy(): void {
    this.subs.map((s) => s.unsubscribe());
  }

  get personNumber(): FormControl {
    return this.form.controls.personNumber as FormControl;
  }

  trackFields(): void {
    this.trackingService.filledField('ssn');
  }

  login(): void {
    if (!this.form.valid) {
      return;
    }

    this.loading = true;
    this.basicInfoService.personNumber = personNumberFormatter(
      this.personNumber.value
    );
    this.subs.push(
      this.companyApi.companyEngagements(this.personNumber.value).subscribe(
        (engagements: CompanyEngagementsModel[]) => {
          if (this.matchSelectedCompanyToEngagements(engagements)) {
            this.handleIdentification(this.personNumber.value);
          } else {
            this.router.navigate(['company-association']);
          }
        },
        (error: any) => this.handleApiError(error)
      )
    );
  }

  matchSelectedCompanyToEngagements(
    engagements: CompanyEngagementsModel[]
  ): boolean {
    return engagements.some(
      (engagement) =>
        engagement.orgNumber === this.basicInfoService.company.orgNumber
    );
  }

  handleIdentification(personNumber: string): void {
    this.trackFields();
    this.subs.push(
      this.loginApi
        .verifyId(personNumber)
        .pipe(
          switchMap((resp: VerifyIdModel) => {
            if (this.deviceService.isMobile()) {
              window.location.replace(
                `bankid:///?autostarttoken=${resp.autoStartToken}`
              );
            }
            return this.pollForVerification(resp.sessionId);
          }),
          switchMap((resp: VerifySessionModel) => {
            this.authService.accessToken = resp.token;
            this.basicInfoService.agreedToTerms = true;
            const { name: firstName, surname: lastName } = resp.userAttributes;
            return this.basicInfoService.handlePostLogin(firstName, lastName);
          })
        )
        .subscribe(
          (data: AddApplicationResponseModel) => {
            this.applicationService.populateServiceWithData(data);
            this.flowService.nextScreen();
          },
          (error: any) => this.handleApiError(error)
        )
    );
  }

  pollForVerification(sessionId: string): Observable<VerifySessionModel> {
    return interval(2 * 1000).pipe(
      timeout(4 * 60 * 1000),
      switchMap(() => this.loginApi.verifySession(sessionId)),
      takeWhile(
        (resp: VerifySessionModel) => this.verifySessionResponse(resp),
        true
      ),
      last()
    );
  }

  verifySessionResponse(session: VerifySessionModel): boolean {
    return !(session.sessionId && session.token);
  }

  handleApiError(error: any): void {
    if (error instanceof TimeoutError) {
      this.error = this.translateService.instant('identification.timeoutError');
    } else {
      this.error = JSON.stringify(error);
    }
    this.loading = false;
  }

  getErrors(): string | null {
    const control = this.form.controls.personNumber;
    if (!control.valid) {
      return this.translateService.instant(
        'identification.personNumberRequiredError'
      );
    }
    return null;
  }

  get currentTicketType(): any {
    return this.basicInfoService.ticketType;
  }
}
